import { createSharedComposable } from '@vueuse/core';
import { getBumpableReadIds, type ReadsApiItem } from '~/utils/editorReadsUtils';
import { useProfileLoginStore } from '~/store/profileLogin';

interface EditorReadsConfig {
  api?: string;
  days?: number;
  minSeconds?: number;
  delayMinutes?: number;
}

interface ReadsApiResponse {
  items?: ReadsApiItem[];
}

function _useEditorReads () {
  const { query } = useTansQuery();
  const { replaceUrl } = useUrlReplace();
  const profileLoginStore = useProfileLoginStore();
  const config = useRuntimeConfig().public.editorReads as EditorReadsConfig | undefined;

  const oneId = computed(() => profileLoginStore.loginStatus.oneId);
  const isLoggedIn = computed(() => profileLoginStore.loginStatus.isLoggedIn);
  const enabled = computed(() => import.meta.client && isLoggedIn.value && !!oneId.value && !!config?.api);

  const api = computed(() => {
    if (!config?.api || !oneId.value) { return ''; }
    return replaceUrl(config.api, { oneId: oneId.value, days: String(config.days ?? 3) });
  });

  const { data, loading } = query({ api, enabled, provider: 'editorReads' });

  const bumpIds = computed<Set<string>>(() => {
    if (!enabled.value) { return new Set(); }
    const items = (data.value as ReadsApiResponse)?.items;
    if (!Array.isArray(items)) { return new Set(); }
    return getBumpableReadIds(items, Date.now(), {
      minSeconds: config?.minSeconds ?? 20,
      delayMinutes: config?.delayMinutes ?? 15
    });
  });

  /*
   * onReadsReady() runs cb once the reads query settles. loading is TanStack's
   * isPending, which flips to false on both success and error (after retries) -
   * the same signal DnCollection relies on to emit - so the Editors Toplist
   * serial chain is never left waiting. cb runs synchronously when the query is
   * disabled or already settled. On error, data stays undefined and bumpIds is
   * empty, so the toplist renders as-is.
   */
  const onReadsReady = (cb: () => void): void => {
    if (!enabled.value || !loading.value) { cb(); return; }
    const stop = watch(loading, (isLoading) => {
      if (!isLoading) {
        stop();
        cb();
      }
    });
  };

  return {
    bumpIds: readonly(bumpIds),
    onReadsReady
  };
}

export const useEditorReads = createSharedComposable(_useEditorReads);
